import React, { useState } from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Qrcode } from "../../../assets/images";
import { 
  faComments, 
  faUserGroup, 
  faBook, 
  faHeadset,
  faTimes,
  faQrcode,
  faArrowRight,
  faShieldAlt,
  faZap,
  faNetworkWired
} from '@fortawesome/free-solid-svg-icons';
import { faDiscord } from '@fortawesome/free-brands-svg-icons';
import { motion, AnimatePresence } from "framer-motion";

const discordInviteLink = "https://discord.gg/umyBUZkW7U";

export default function ChatRoomHub() {
  const [showQRPopup, setShowQRPopup] = useState(false);
  const toggleQRPopup = () => setShowQRPopup(!showQRPopup);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 100 } as any
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFB] p-2 md:p-6 lg:p-10">
      
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10 max-w-[1600px] mx-auto">
        <div>
           <h1 className="text-3xl md:text-5xl font-black text-[#05322B] tracking-tighter uppercase italic">
             Chat <span className="text-[#10B981]">Room</span>
           </h1>
           <p className="text-[10px] md:text-[11px] font-bold text-gray-400 uppercase tracking-[0.4em] mt-2 ml-1">
             VOTRE HUB DE COMMUNICATION EXCLUSIF
           </p>
        </div>
        
        <div className="hidden sm:flex items-center gap-4 bg-white/80 backdrop-blur-md p-3 rounded-2xl border border-white shadow-sm self-start md:self-auto">
           <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-500 flex items-center justify-center">
              <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div>
           </div>
           <div>
              <p className="text-[10px] font-black text-[#05322B] uppercase tracking-widest leading-none mb-1">Status Live</p>
              <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest leading-none">24 Professeurs en ligne</p>
           </div>
        </div>
      </div>

      <motion.div 
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 max-w-[1600px] mx-auto"
      >
        
        {/* Main Hero Card: Discord Integration */}
        <motion.div variants={itemVariants} className="lg:col-span-8 group relative rounded-[2.5rem] lg:rounded-[3.5rem] overflow-hidden shadow-[0_32px_128px_rgba(16,185,129,0.12)] border-2 lg:border-4 border-white bg-white">
           <div className="lg:flex min-h-[450px] lg:min-h-[550px]">
              <div className="lg:w-[35%] bg-gradient-to-br from-[#5865F2] to-[#4752C4] flex flex-col items-center justify-center p-10 relative overflow-hidden">
                 <div className="absolute inset-0 opacity-10 flex flex-wrap gap-4 p-4 pointer-events-none">
                    {Array.from({length: 30}).map((_, i) => (
                      <FontAwesomeIcon key={i} icon={faDiscord} className="text-2xl animate-pulse" style={{ animationDelay: `${i * 150}ms` }} />
                    ))}
                 </div>

                 <motion.div 
                   className="w-24 h-24 lg:w-32 lg:h-32 rounded-[2rem] lg:rounded-[2.5rem] bg-white flex items-center justify-center text-[#5865F2] shadow-2xl z-20"
                   animate={{ y: [0, -10, 0] }}
                   transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                 >
                    <FontAwesomeIcon icon={faDiscord} className="text-5xl lg:text-6xl" />
                 </motion.div>
                 
                 <div className="mt-6 text-center z-20 text-white">
                    <h3 className="font-black text-xl lg:text-2xl uppercase tracking-tighter mb-1 italic">FOK NJE7EK</h3>
                    <p className="opacity-60 text-[9px] uppercase font-black tracking-[0.3em]">Serveur Officiel</p>
                 </div>
              </div>

              <div className="lg:w-[65%] p-8 lg:p-14 flex flex-col justify-center relative bg-white">
                 <h2 className="text-3xl lg:text-5xl font-black text-[#05322B] uppercase tracking-tighter leading-[0.9] mb-6">
                   Rejoignez-nous sur <span className="text-[#5865F2]">Discord</span>
                 </h2>
                 <p className="text-[#05322B]/60 font-medium text-base lg:text-lg mb-10 leading-relaxed max-w-[90%]">
                   Collaborez en temps réel, participez à des sessions de coaching vocal et accédez à des ressources exclusives.
                 </p>

                 <div className="flex flex-col sm:flex-row gap-4 mb-10">
                    <motion.a 
                      href={discordInviteLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-3 h-14 lg:h-16 px-8 bg-[#5865F2] text-white rounded-[1.25rem] font-black text-[10px] md:text-[11px] uppercase tracking-wider shadow-xl shadow-indigo-500/20 hover:shadow-indigo-500/40 hover:-translate-y-1 transition-all duration-300"
                      whileHover={{ scale: 1.02 }}
                    >
                       <span className="whitespace-nowrap">Rejoindre maintenant</span>
                       <FontAwesomeIcon icon={faArrowRight} className="text-[10px]" />
                    </motion.a>
                    
                    <button 
                      onClick={toggleQRPopup}
                      className="inline-flex items-center justify-center gap-3 h-14 lg:h-16 px-6 lg:px-8 bg-slate-50 text-gray-400 rounded-[1.25rem] font-black text-[10px] md:text-[11px] uppercase tracking-wider border border-slate-100 hover:bg-slate-100 transition-all duration-300"
                    >
                       <FontAwesomeIcon icon={faQrcode} />
                       <span className="whitespace-nowrap">QR Code</span>
                    </button>
                 </div>

                 <div className="pt-8 border-t border-slate-50 flex flex-wrap gap-x-8 gap-y-4">
                    <div className="flex items-center gap-2 pr-4 border-r border-slate-100">
                       <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-500 flex items-center justify-center text-xs">
                          <FontAwesomeIcon icon={faZap} />
                       </div>
                       <span className="text-[9px] font-black text-[#05322B]/40 uppercase tracking-widest">Instantané</span>
                    </div>
                    <div className="flex items-center gap-2 pr-4 border-r border-slate-100">
                       <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-500 flex items-center justify-center text-xs">
                          <FontAwesomeIcon icon={faShieldAlt} />
                       </div>
                       <span className="text-[9px] font-black text-[#05322B]/40 uppercase tracking-widest">Sécurisé</span>
                    </div>
                    <div className="flex items-center gap-2">
                       <div className="w-8 h-8 rounded-lg bg-purple-50 text-purple-500 flex items-center justify-center text-xs">
                          <FontAwesomeIcon icon={faNetworkWired} />
                       </div>
                       <span className="text-[9px] font-black text-[#05322B]/40 uppercase tracking-widest">Communauté VIP</span>
                    </div>
                 </div>
              </div>
           </div>
        </motion.div>

        {/* Benefits Sidebar */}
        <motion.div variants={itemVariants} className="lg:col-span-4 flex flex-col gap-6">
           <div className="bg-white/60 backdrop-blur-xl p-8 rounded-[2.5rem] border-2 border-white shadow-xl flex-1 flex flex-col">
              <h3 className="text-[11px] font-black text-[#05322B] uppercase tracking-[0.4em] mb-10 flex items-center gap-3">
                 <div className="w-1.5 h-6 bg-[#10B981] rounded-full"></div>
                 POURQUOI NOUS REJOINDRE ?
              </h3>
              
              <div className="space-y-8 flex-1">
                 <BenefitItem 
                   icon={faComments} 
                   color="bg-emerald-50 text-emerald-500"
                   title="Interaction Directe"
                   desc="Chattez en direct avec vos professeurs."
                 />
                 <BenefitItem 
                   icon={faHeadset} 
                   color="bg-blue-50 text-blue-500"
                   title="Salons Vocaux"
                   desc="Ateliers live et coaching vocal."
                 />
                 <BenefitItem 
                   icon={faUserGroup} 
                   color="bg-purple-50 text-purple-500"
                   title="Entraide"
                   desc="Communauté active 24h/24."
                 />
                 <BenefitItem 
                   icon={faBook} 
                   color="bg-amber-50 text-amber-500"
                   title="Cours Exclusifs"
                   desc="Documents VIP uniquement sur Discord."
                 />
              </div>

              <div className="mt-10 bg-[#05322B] p-8 rounded-[2rem] relative overflow-hidden group">
                <div className="absolute inset-0 bg-gradient-to-br from-[#10B981]/5 to-transparent"></div>
                <h4 className="text-white font-black text-xl uppercase tracking-tighter relative z-10 leading-[0.85]">
                  PRÊT POUR LA <span className="text-[#10B981]">RÉUSSITE</span> ?
                </h4>
                <p className="text-[#10B981]/60 text-[9px] font-bold uppercase tracking-[0.2em] mt-3 relative z-10">
                  Boostez vos résultats aujourd'hui.
                </p>
              </div>
           </div>
        </motion.div>
      </motion.div>

      {/* QR Popup */}
      <AnimatePresence>
        {showQRPopup && (
          <motion.div 
            className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-[#05322B]/70 backdrop-blur-md"
            onClick={toggleQRPopup}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div 
              className="w-full max-w-[400px] max-h-[90vh] bg-white rounded-[2.5rem] p-6 md:p-8 shadow-3xl overflow-y-auto relative scrollbar-hide"
              onClick={e => e.stopPropagation()}
              initial={{ scale: 0.9, y: 30, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.9, y: 30, opacity: 0 }}
            >
              <button 
                className="absolute top-5 right-5 w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center text-gray-400 hover:text-rose-500 transition-all cursor-pointer z-50 shadow-sm"
                onClick={toggleQRPopup}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>

              <div className="flex flex-col items-center pt-2">
                 <div className="w-16 h-16 bg-[#5865F2]/10 text-[#5865F2] rounded-2xl flex items-center justify-center text-3xl mb-4">
                    <FontAwesomeIcon icon={faDiscord} />
                 </div>
                 <h3 className="text-xl font-black text-[#05322B] uppercase tracking-tighter mb-1">FOK NJE7EK</h3>
                 <p className="text-[10px] text-gray-400 font-bold uppercase tracking-[0.2em] mb-4">Rejoignez-nous sur mobile</p>
                 
                 <div className="w-full relative group">
                    <img src={Qrcode} alt="QR Code" className="w-full h-auto object-contain rounded-2xl" />
                 </div>

                 <div className="mt-6 flex flex-col items-center gap-3">
                    <a href={discordInviteLink} target="_blank" rel="noreferrer" className="text-[11px] font-black text-[#5865F2] uppercase tracking-[0.2em] hover:underline transition-all">
                      Lien direct →
                    </a>
                 </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
}

const BenefitItem = ({ icon, color, title, desc }: { icon: any, color: string, title: string, desc: string }) => (
  <motion.div 
    className="flex items-start gap-4"
    whileHover={{ x: 5 }}
  >
     <div className={`w-11 h-11 lg:w-14 lg:h-14 shrink-0 rounded-2xl flex items-center justify-center text-xl lg:text-2xl shadow-lg shadow-black/5 ${color}`}>
        <FontAwesomeIcon icon={icon} />
     </div>
     <div className="pt-1 text-[#05322B]">
        <h5 className="text-[13px] lg:text-[14px] font-black uppercase tracking-tight mb-0.5">{title}</h5>
        <p className="text-[10px] lg:text-[11px] font-medium text-gray-400 leading-snug">{desc}</p>
     </div>
  </motion.div>
);